import { useState } from 'react'
import dayjs from 'dayjs'
import { X, FilePlus } from '@/components/icons'
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from './ui/drawer'
import { Button } from './ui/button'
import {
  DocumentListCard,
  getDocumentExpiryStatus,
  getDocumentIcon,
} from './DocumentListCard'
import { EmptyState } from './EmptyState'
import { DocumentFormDrawer } from './documents/DocumentFormDrawer'
import { ViewDocumentDrawer } from './documents/ViewDocumentDrawer'
import { useAircraftGetById } from '../lib/hooks'
import { useIsAdmin } from '@/hooks/useIsCompanyAdmin'
import { hapticLight } from '@/lib/haptics'
import { documentSubTypeLabels } from '@@api/shared'

interface AircraftDocumentsDrawerProps {
  aircraftId: string
  open: boolean
  onClose: () => void
}

export function AircraftDocumentsDrawer({
  aircraftId,
  open,
  onClose,
}: AircraftDocumentsDrawerProps) {
  const { data: aircraft } = useAircraftGetById(aircraftId)
  const { isAdmin } = useIsAdmin()
  const [isAddDocumentDrawerOpen, setIsAddDocumentDrawerOpen] = useState(false)
  const [selectedDocumentId, setSelectedDocumentId] = useState<string | null>(null)
  // Separate open flag; the id is kept on close so the view drawer can play
  // its exit animation (the next tap overwrites it).
  const [isViewDocumentOpen, setIsViewDocumentOpen] = useState(false)

  const documents = aircraft?.documents ?? []

  const handleAdd = () => {
    hapticLight()
    setIsAddDocumentDrawerOpen(true)
  }

  return (
    <>
      <Drawer open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
        <DrawerContent className="bg-surface-L0 flex flex-col max-h-[85dvh]">
          <DrawerHeader className="flex flex-row items-center justify-between p-4 shrink-0">
            <DrawerTitle className="text-lg font-semibold text-content-primary-bold">
              Documents
            </DrawerTitle>
            <Button
              variant="secondary"
              size="icon"
              roundness="round"
              className="size-8"
              onClick={onClose}
            >
              <X className="w-4 h-4" />
            </Button>
          </DrawerHeader>

          <div
            className="min-h-0 flex-1 overflow-y-auto px-4 pb-8"
            style={{ WebkitOverflowScrolling: 'touch' }}
          >
            {documents.length > 0 ? (
              <div className="flex flex-col gap-3">
                {documents.map((doc) => {
                  const DocumentIcon = getDocumentIcon(doc.subType)
                  const expiryStatus = doc.expiryDate
                    ? getDocumentExpiryStatus(doc.expiryDate)
                    : undefined
                  const expiryLabel = doc.expiryDate
                    ? `Expires ${dayjs(doc.expiryDate).format('MMM D, YYYY')}`
                    : 'Expiry date not set'
                  return (
                    <DocumentListCard
                      key={doc.id}
                      icon={<DocumentIcon />}
                      title={doc.title}
                      subTypeLabel={documentSubTypeLabels[doc.subType] || doc.subType}
                      expiryLabel={expiryLabel}
                      expiryStatus={expiryStatus}
                      onClick={() => {
                        setSelectedDocumentId(doc.id)
                        setIsViewDocumentOpen(true)
                      }}
                    />
                  )
                })}
                {isAdmin && (
                  <Button className="w-full mt-2" onClick={handleAdd}>
                    Add Document
                  </Button>
                )}
              </div>
            ) : (
              <EmptyState
                icon={FilePlus}
                title="No documents yet"
                description="Upload manuals, SOPs, checklists, policies, or licenses for this aircraft."
                addButtonText={isAdmin ? 'Add Document' : undefined}
                onAdd={isAdmin ? handleAdd : undefined}
              />
            )}
          </div>
        </DrawerContent>
      </Drawer>

      <DocumentFormDrawer
        isOpen={isAddDocumentDrawerOpen}
        owner={{ type: 'aircraft', aircraftId }}
        onClose={() => setIsAddDocumentDrawerOpen(false)}
      />

      <ViewDocumentDrawer
        isOpen={isViewDocumentOpen}
        onClose={() => setIsViewDocumentOpen(false)}
        documentId={selectedDocumentId}
      />
    </>
  )
}
